import { formatMoneyDe, isIsoCurrency } from '../currencies.ts';
import { packUnitText, type PackUnitFacts } from '../pack-unit.ts';
import type { ListColumn, ListMoneyValue, ListRelationFilter } from './contract.ts';
import { listSignalsText } from './signals.ts';
import { COMPARE_COLUMN, operatorLabels, operatorValueKind, type ListCondition, type ListFilter, type ListFilterOperator, type ListRelationCondition } from './filter.ts';

/**
 * Text der Filter-Pille (EXPERIENCE §4.9): Spalte und Wert ausgeschrieben, z. B. „Land: Österreich, Italien“,
 * „verfügbar < 5“, „Kundennummer = 99999“. Rein; Zahlen und Daten im deutschen Format.
 */

const numberFormat = new Intl.NumberFormat('de-DE', { maximumFractionDigits: 4 });
const signedFormat = new Intl.NumberFormat('de-DE', { maximumFractionDigits: 4, signDisplay: 'exceptZero' });
/** Einmal angelegt: ein neues `Intl.DateTimeFormat` je Zelle kostete beim Aufbau großer Listen spürbar Zeit. */
const dateTimeFormat = new Intl.DateTimeFormat('de-DE', { timeZone: 'Europe/Berlin', day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
/** Mit `signed`: „+20“ bzw. „−2“ (echtes Minuszeichen), sonst deutsches Zahlformat. */
const formatNumber = (value: number, signed: boolean | undefined) => (signed ? signedFormat.format(value).replace('-', '−') : numberFormat.format(value));
/** Ist der Wert ein Betrag mit Währung (`ListMoneyValue`)? */
export function isListMoneyValue(value: unknown): value is ListMoneyValue {
  return typeof value === 'object' && value !== null && typeof (value as ListMoneyValue).amountMinor === 'string' && typeof (value as ListMoneyValue).currency === 'string';
}

/** `2027-02-14` → `14.02.2027`. */
export function formatListDate(isoDate: string): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(isoDate);
  return m ? `${m[3]}.${m[2]}.${m[1]}` : isoDate;
}

/**
 * Ein Wert einer Spalte als Text (für Pille und Zelle). Beträge kommen in der kleinsten Einheit (Cent). Mengen mit der
 * Packeinheit der Zeile (`unit`, Ticket 1.270 Änderung 3: Einzahl/Mehrzahl nach dem Wert), ohne Packeinheit „Stück“.
 */
export function formatListValue(column: Pick<ListColumn, 'type' | 'options' | 'signed'>, value: unknown, unit?: PackUnitFacts | null): string {
  if (value === null || value === undefined || value === '') return '–';
  switch (column.type) {
    case 'number':
      // Kein Zahlenwert (z. B. „Standard“ beim Zahlungsziel ohne gepflegte Konditionen, Story 4.15): Text unverändert, nie „NaN“.
      return Number.isNaN(Number(value)) ? String(value) : formatNumber(Number(value), column.signed);
    case 'quantity':
      // Mit Vorzeichen bedeutet 0 „keine Änderung“ (z. B. Menge einer Zuteilung): „–“ wie ein leerer Wert.
      if (column.signed && Number(value) === 0) return '–';
      return `${formatNumber(Number(value), column.signed)} ${packUnitText(unit, Math.abs(Number(value)))}`;
    case 'percent':
      return `${numberFormat.format(Number(value))} %`;
    case 'money':
      // Betrag mit seiner Währung (Zelle und Filterwert), Nachkommastellen nach ISO 4217.
      return isListMoneyValue(value) && /^-?\d+$/.test(value.amountMinor) && isIsoCurrency(value.currency)
        ? formatMoneyDe(BigInt(value.amountMinor), value.currency)
        : String(value);
    case 'date':
      return formatListDate(String(value));
    case 'datetime': {
      const d = new Date(String(value));
      if (Number.isNaN(d.getTime())) return String(value);
      return dateTimeFormat.format(d).replace(',', '');
    }
    case 'boolean':
      return value === true ? 'ja' : 'nein';
    case 'enum':
    case 'status':
      return optionLabel(column, String(value));
    case 'signals':
      // Nur die nicht-grünen Signale als Text (Story 7.23); alles grün → leer.
      return listSignalsText(column, value);
    case 'multi_enum':
      return Array.isArray(value) ? value.map((v) => optionLabel(column, String(v))).join(', ') : optionLabel(column, String(value));
    default:
      return String(value);
  }
}

/** Beschriftung eines Auswahlwerts; unbekannte Werte als Wert selbst. */
export function optionLabel(column: Pick<ListColumn, 'options'>, value: string): string {
  return column.options?.find((o) => o.value === value)?.label ?? value;
}

function valueText(column: Pick<ListColumn, 'type' | 'options'>, value: unknown): string {
  if (column.type === 'text') return `„${String(value)}“`;
  if (column.type === 'datetime') return formatListDate(String(value));
  return formatListValue(column, value);
}

/** Pillentext eines Filters, z. B. „Land: Österreich, Italien“. */
export function describeFilter(column: Pick<ListColumn, 'label' | 'type' | 'options'>, filter: Pick<ListFilter, 'op' | 'value'>): string {
  const { label } = column;
  const v = filter.value;
  switch (filter.op) {
    case 'in':
      return `${label}: ${listText(column, v)}`;
    case 'not_in':
      return `${label}: ohne ${listText(column, v)}`;
    case 'contains_any':
      return `${label}: eine von ${listText(column, v)}`;
    case 'contains_all':
      return `${label}: alle von ${listText(column, v)}`;
    case 'contains_none':
      return `${label}: keine von ${listText(column, v)}`;
    case 'is_true':
      return `${label}: ja`;
    case 'is_false':
      return `${label}: nein`;
    case 'is_empty':
      return `${label} ist leer`;
    case 'is_not_empty':
      return `${label} ist nicht leer`;
    case 'today':
    case 'last_7_days':
    case 'last_30_days':
    case 'this_month':
    case 'last_month':
      return `${label}: ${operatorLabels[filter.op]}`;
    case 'contains':
      return `${label} enthält ${valueText(column, v)}`;
    case 'starts_with':
      return `${label} beginnt mit ${valueText(column, v)}`;
    case 'equals':
      return `${label} = ${String(v)}`;
    case 'before':
    case 'after':
      return `${label} ${operatorLabels[filter.op]} ${valueText(column, v)}`;
    case 'between': {
      const range = (v ?? {}) as { from?: unknown; to?: unknown };
      if (column.type === 'date' || column.type === 'datetime') {
        return `${label}: ${valueText(column, range.from)} – ${valueText(column, range.to)}`;
      }
      return `${label} zwischen ${valueText(column, range.from)} und ${valueText(column, range.to)}`;
    }
    default:
      if (operatorValueKind(filter.op) === 'scalar') return `${label} ${operatorLabels[filter.op]} ${valueText(column, v)}`;
      return `${label}: ${operatorLabels[filter.op]}`;
  }
}

function listText(column: Pick<ListColumn, 'options'>, value: unknown): string {
  const values = Array.isArray(value) ? value : value === undefined ? [] : [value];
  return values.map((x) => optionLabel(column, String(x))).join(', ');
}

/**
 * Pillentext einer Bedingung der Version 2 (Story 1.32): wie `describeFilter`, dazu „vor mehr als 90 Tagen“, „in den
 * nächsten 7 Tagen“, Spaltenvergleich („verfügbar < Mindestbestand“) und „nicht: …“. `other` = Vergleichsspalte.
 */
export function describeCondition(
  column: Pick<ListColumn, 'label' | 'type' | 'options'>,
  condition: Pick<ListCondition, 'op' | 'value' | 'not'>,
  other?: Pick<ListColumn, 'label'>,
): string {
  const { label } = column;
  const v = condition.value;
  let text: string;
  if (condition.op === 'older_than_days') text = `${label}: vor mehr als ${daysText(v)}`;
  else if (condition.op === 'within_next_days') text = `${label}: in den nächsten ${daysText(v)}`;
  else if (condition.op === COMPARE_COLUMN) {
    const compare = (v ?? {}) as { op?: string; column?: string };
    const symbol = compare.op && compare.op in operatorLabels ? operatorLabels[compare.op as ListFilterOperator] : '?';
    text = `${label} ${symbol} ${other?.label ?? compare.column ?? '?'}`;
  } else text = describeFilter(column, { op: condition.op, value: v as ListFilter['value'] });
  return condition.not ? `nicht: ${text}` : text;
}

function daysText(value: unknown): string {
  const n = Number(value);
  return n === 1 ? '1 Tag' : `${numberFormat.format(n)} Tagen`;
}

const PERIOD_WORDS = { short: 'kurzer Zeitraum', medium: 'mittlerer Zeitraum', long: 'langer Zeitraum' } as const;

/**
 * Text der Pille eines Beziehungsfilters (Story 1.33), z. B. „hat gekauft: Artikel TT-VF-Z4-0800 · letzte 90 Tage ·
 * mindestens 5“ bzw. „hat nie gekauft · Vorjahr letzte 30 Tage“. `periods`: Tage der Zeiträume (Einstellungen), sonst
 * nur das Wort. Ohne angemeldeten Filter: Schlüssel.
 */
export function describeRelation(
  relation: (Pick<ListRelationFilter, 'hasLabel' | 'hasNotLabel'> & { targets: readonly { key: string; label: string }[] }) | null,
  condition: Pick<ListRelationCondition, 'relation' | 'has' | 'target' | 'period' | 'minQuantity'>,
  periods?: { short: number; medium: number; long: number },
): string {
  if (!relation) return condition.relation;
  const parts: string[] = [];
  let head = condition.has ? relation.hasLabel : relation.hasNotLabel;
  if (condition.target) {
    const target = relation.targets.find((t) => t.key === condition.target!.key);
    head += `: ${target?.label ?? condition.target.key} ${condition.target.values.join(', ')}`;
  }
  parts.push(head);
  if (condition.period) {
    const days = 'window' in condition.period ? periods?.[condition.period.window] : condition.period.days;
    const text =
      days === undefined ? PERIOD_WORDS[(condition.period as { window: keyof typeof PERIOD_WORDS }).window] : days === 1 ? 'heute' : `letzte ${numberFormat.format(days)} Tage`;
    parts.push(condition.period.previousYear ? `Vorjahr ${text}` : text);
  }
  if (condition.minQuantity !== undefined) parts.push(`mindestens ${numberFormat.format(condition.minQuantity)}`);
  return parts.join(' · ');
}
